import { useEffect, useRef, useState } from "react";
import DetailsCard from "@/components/DetailsCard";
import DetailsCardTwoColumnRow from "@/components/DetailsCardTwoColumnRow";
import MultiSectionDetailsCard from "@/components/MultiSectionDetailsCard";
import { Person } from "@/types";
import {
  addressOptions,
  emailOptions,
  facilitySections,
  officialDetailsOptions,
  orphanedContactPreferenceOption,
  personalOptions,
  phoneOptions,
  sectionOptions
} from "../../utils/utils";
import { useCanManagePeople, useCanManageChapters, useCanEditEmail, useCanEditLastName } from "@/hooks/useCapabilities";
import PhoneEditForm from "./forms/PhoneEditForm";
import { useErrorBanner } from "@/providers/ErrorBannerProvider";
import AddressEditForm from "./forms/AddressEditForm";
import PersonalEditForm from "./forms/PersonalEditForm";
import OfficialEditForm from "./forms/OfficialEditForm";
import EmailEditModal from "./modals/EmailEditModal";
import UpdatePreferencesModal from "./modals/UpdatePreferencesModal";
import SectionHeading from "@/components/SectionHeading";
import SectionChapterAffiliationsEditForm from "./forms/SectionChapterAffiliationsEditForm";
import { showSnackbar } from "@/lib/snackbarUtils";

export interface UpdatedFields {
  mobilePhone?: boolean;
  homePhone?: boolean;
  email?: boolean;
  homeAddress?: boolean;
  directoryOptOut?: boolean;
}

interface ContactDetailsProps {
  person: Person;
  updatedFields?: UpdatedFields;
  suppressInlineEditing?: boolean;
}

export function ContactDetails({ person, updatedFields, suppressInlineEditing = false }: ContactDetailsProps) {
  const canEdit = useCanManagePeople() && !suppressInlineEditing;
  const canManageChapters = useCanManageChapters() && !suppressInlineEditing;
  const canEditEmail = useCanEditEmail() && !suppressInlineEditing;
  const canEditLastName = useCanEditLastName() && !suppressInlineEditing;
  const { setError, clearError } = useErrorBanner();
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [currentPerson, setCurrentPerson] = useState<Person>(person);
  // Holds an external person update that arrives while a section form is open.
  // Applied when the form closes (or the open section changes) so a skipped
  // update is never lost; saved form values merge on top so form-owned fields
  // always win.
  const pendingPersonRef = useRef<Person | null>(null);
  const [isPreferencesModalOpen, setIsPreferencesModalOpen] = useState(false);
  const [emailEditOpen, setEmailEditOpen] = useState(false);
  const cardEditingSection = emailEditOpen ? "email-modal" : editingSection;

  const applyPendingPerson = () => {
    if (pendingPersonRef.current) {
      setCurrentPerson(pendingPersonRef.current);
      pendingPersonRef.current = null;
    }
  };
  const clearEditingSection = () => {
    setEditingSection(null);
    applyPendingPerson();
  };
  const closeEmailEdit = () => {
    setEmailEditOpen(false);
    applyPendingPerson();
  };

  // External cache writes (e.g. accepting an AI nickname suggestion) arrive as a new
  // person prop. Fold them in; local edit-form merges survive because the query hook
  // keeps the prop referentially stable when data is unchanged. While a form is open
  // the update is stashed and applied on close, so a form seeded from older values
  // can't overwrite it last-write-wins — and the skipped update is never dropped.
  useEffect(() => {
    if (cardEditingSection) {
      pendingPersonRef.current = person;
      return;
    }
    pendingPersonRef.current = null;
    setCurrentPerson(person);
    // cardEditingSection intentionally omitted: syncing on form open/close
    // would clobber local edit-form merges that never reach the cache.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [person]);

  const handleSectionChange = (section: string | null) => {
    applyPendingPerson();
    if (section === "Email") {
      setEmailEditOpen(true);
      return;
    }
    setEmailEditOpen(false);
    setEditingSection(section);
  };

  const onSuccess = (updatedPerson: Partial<Person>) => {
    clearError();
    clearEditingSection();
    closeEmailEdit();
    setCurrentPerson((prevPerson) => ({
      ...prevPerson,
      ...updatedPerson
    }));
    showSnackbar("Contact details updated");
  };

  const onOfficialSuccess = (updatedPerson: Partial<Person>) => {
    clearError();
    clearEditingSection();
    closeEmailEdit();
    setCurrentPerson((prevPerson) => ({
      ...prevPerson,
      ...updatedPerson
    }));
    showSnackbar("Official data updated");
  };

  const onError = (error: string) => {
    clearEditingSection();
    setError(error);
  };

  const openPreferencesModal = () => {
    setIsPreferencesModalOpen(true);
  };

  const closePreferencesModal = () => {
    setIsPreferencesModalOpen(false);
  };

  const orphanedContactPreference = orphanedContactPreferenceOption(currentPerson);
  const addressSectionOptions = [
    ...addressOptions(currentPerson.addresses, currentPerson.contactAddressId),
    ...(orphanedContactPreference ? [orphanedContactPreference] : [])
  ];

  return (
    <div data-testid="contact-details">
      {canEdit && (
        <SectionHeading
          actions={[
            {
              label: "Update Preferences",
              onClick: openPreferencesModal,
              testId: "update-preferences-button"
            }
          ]}
        />
      )}
      <DetailsCardTwoColumnRow
        tabBodySpacing={!canEdit}
        narrowColumn={
          <MultiSectionDetailsCard
            editingSection={cardEditingSection}
            setEditingSection={handleSectionChange}
            sections={[
              {
                heading: "Personal",
                options: personalOptions(currentPerson).map((opt) => ({
                  ...opt,
                  ...(opt.overline === "PGA Directory Visibility" &&
                    updatedFields?.directoryOptOut != null && {
                      isUpdated: updatedFields.directoryOptOut
                    })
                })),
                ...(canEdit && {
                  renderEdit: () => (
                    <PersonalEditForm
                      personId={currentPerson.id}
                      onCancel={clearEditingSection}
                      onSuccess={onSuccess}
                      onError={onError}
                      informalName={currentPerson.informalName}
                      preferredLastName={currentPerson.preferredLastName}
                      dob={currentPerson.dob}
                      gender={currentPerson.gender}
                      ssn={currentPerson.ssn?.full}
                      spouseName={currentPerson.spouseName ?? undefined}
                      eventRegistrationParentalConsent={currentPerson.eventRegistrationParentalConsent}
                      age={currentPerson.age}
                      latestProgram={currentPerson.latestProgram}
                    />
                  )
                })
              },
              {
                heading: "Official",
                options: officialDetailsOptions(currentPerson, canEditLastName),
                ...(canEdit && {
                  renderEdit: () => (
                    <OfficialEditForm
                      personId={currentPerson.id}
                      pgaId={currentPerson.pgaId}
                      firstName={currentPerson.firstName}
                      lastName={currentPerson.lastName}
                      middleName={currentPerson.middleName}
                      suffixName={currentPerson.suffixName}
                      canEditLastName={canEditLastName}
                      onCancel={clearEditingSection}
                      onSuccess={onOfficialSuccess}
                      onError={onError}
                    />
                  )
                })
              }
            ]}
          />
        }
        wideColumn={
          <>
            <MultiSectionDetailsCard
              editingSection={cardEditingSection}
              setEditingSection={handleSectionChange}
              sections={[
                {
                  heading: "Phone",
                  options: phoneOptions(currentPerson.phoneNumbers).map((opt) => ({
                    ...opt,
                    ...(opt.overline === "Mobile" &&
                      updatedFields?.mobilePhone != null && {
                        isUpdated: updatedFields.mobilePhone
                      }),
                    ...(opt.overline === "Home" &&
                      updatedFields?.homePhone != null && {
                        isUpdated: updatedFields.homePhone
                      })
                  })),
                  ...(canEdit && {
                    renderEdit: () => (
                      <PhoneEditForm
                        personId={currentPerson.id}
                        phoneNumbers={currentPerson.phoneNumbers}
                        onCancel={clearEditingSection}
                        onSuccess={onSuccess}
                        onError={onError}
                      />
                    )
                  })
                },
                {
                  heading: "Email",
                  options: emailOptions(currentPerson.email).map((opt) => ({
                    ...opt,
                    ...(updatedFields?.email != null && {
                      isUpdated: updatedFields.email
                    })
                  })),
                  ...(canEditEmail && {
                    renderEdit: () => null
                  })
                }
              ]}
            />

            <MultiSectionDetailsCard
              editingSection={cardEditingSection}
              setEditingSection={handleSectionChange}
              sections={[
                {
                  heading: "Address",
                  options: addressSectionOptions.map((opt) => ({
                    ...opt,
                    ...(opt.overline === "Home Address" &&
                      updatedFields?.homeAddress != null && {
                        isUpdated: updatedFields.homeAddress
                      })
                  })),
                  ...(canEdit && {
                    renderEdit() {
                      return (
                        <AddressEditForm
                          personId={currentPerson.id}
                          addresses={currentPerson.addresses}
                          onCancel={clearEditingSection}
                          onSuccess={onSuccess}
                          onError={onError}
                        />
                      );
                    }
                  })
                },
                ...facilitySections(currentPerson.employments, currentPerson.contactAddressId)
              ]}
            />
            {currentPerson.primarySection && (
              <DetailsCard
                heading="Section"
                options={sectionOptions(currentPerson, true)}
                editingSection={cardEditingSection}
                {...(canManageChapters && {
                  onEdit: () => handleSectionChange("Section"),
                  renderEdit: () => (
                    <SectionChapterAffiliationsEditForm
                      person={currentPerson}
                      onCancel={clearEditingSection}
                      onSuccess={onSuccess}
                      onError={onError}
                    />
                  )
                })}
              />
            )}
            {person.militaryService && (
              <DetailsCard
                heading="Military"
                options={[
                  {
                    iconName: "military_tech",
                    overline: "Status",
                    headline: person.militaryService.status
                  }
                ]}
                editingSection={cardEditingSection}
              />
            )}
          </>
        }
      />
      {canEditEmail && emailEditOpen && (
        <EmailEditModal
          open={emailEditOpen}
          personId={currentPerson.id}
          email={currentPerson.email}
          identityPerson={currentPerson}
          onCancel={closeEmailEdit}
          onSuccess={onSuccess}
          onError={onError}
        />
      )}
      {canEdit && (
        <UpdatePreferencesModal
          person={currentPerson}
          open={isPreferencesModalOpen}
          onClose={closePreferencesModal}
          onSuccess={onSuccess}
        />
      )}
    </div>
  );
}
